import * as React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Grid, Typography, Chip, TextField, Select, MenuItem, FormControl, InputLabel, Button as MUIButton,
  FormControlLabel,
  RadioGroup,
  Radio
} from "@mui/material";
import { BadgePercent, Clock, Loader2, TrendingDown, AlertTriangle, X } from 'lucide-react';
import { cn, convertToJsonObject, mapping } from '../../utils/utils';
import { useState } from 'react';
import Cookies from 'js-cookie'
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import amplitude from "../../utils/ampli";
import { setAlertText, setSeverity, setToast } from '../../redux/slices/commonComponentSlice';
import { apiSlice, useGetFilteredDataMutation } from '../../redux/slices/apiSlice';
import { startProgress } from '../../redux/slices/progressSlice';
import { pricing } from '../../redux/slices/pricingSlice';
import { auth } from '../../redux/slices/authSlice';

// Reuse your TailwindTabs but we’ll only show the first 3 tabs (PPC / Total / Rap Disc)
function PriceTypeTabs({ value, onChange }) {
  const tabs = [
    { id: 0, label: "PPC" },
    { id: 1, label: "Total Price" },
    { id: 2, label: "Rap Disc" },
  ];
  return (
    <div className="w-full border-b border-gray-500">
      <div className="flex border border-gray-100 bg-transparent px-[0.1rem] gap-1 py-[0.11rem] rounded-md justify-between items-center">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            onClick={() => onChange(null, tab.id)}
            className={`
              relative px-1 py-[0.35rem] text-center w-full rounded-md text-xs font-semibold transition-colors duration-200
               cursor-pointer
              ${value === tab.id 
                ? 'bg-blue-600 border-b-2 text-white border-blue-600 bg-blue-50/30 hover:bg-blue-700' 
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
              }
            `}
          >
            {tab.label}
          </div>
        ))}
      </div>
    </div>
  );
}

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const fmtMoney = (n, opts = { decimals: 0 }) => {
  if (!Number.isFinite(n)) return '—';
  const { decimals = 0 } = opts || {};
  return `$${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
};

const labelFor = (t) => (t === 0 ? 'PPC' : t === 1 ? 'Total Price' : 'RAP %');

export default function BulkAutoPricing({
  selectedItems, filters, searchParam, allSelected,handleBulkClose, activeTab,configSort, id
}) {
   
  // Core inputs
  const [priceType, setPriceType] = React.useState(1);          // 0=ppc, 1=total, 2=rap
  const [targetValue, setTargetValue] = React.useState("");     // per-car, total $, or rap %
  // New 3 inputs
  const [pricingMethod, setPricingMethod] = useState("to")
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
    
    // Progress tracking states
    const [isProcessing, setIsProcessing] = useState(false);  
    const dispatch = useDispatch();
    const sorting = useSelector(pricing).sorting;
    const page = useSelector(pricing).page;
    const pageSize = useSelector(pricing).pageSize;
    const accountId = useSelector(auth)?.userDetails?.account_id;
    const [refetchFiltered] = useGetFilteredDataMutation();
    const customFiltersRaw = useSelector(pricing)?.config?.custom_filters ?? [];
  
  
    
    let newFilters = convertToJsonObject(filters, customFiltersRaw);
  
    // Move this before the useEffect that uses it
    const { uniqueLDIDs, uniqueCertNums, uniquePRRIDs, uniquePRR1IDs } = React.useMemo(() => {
      const ldIds = new Set();
      const certNums = new Set();
      const prrNums = new Set();
      selectedItems.forEach(item => {
        ldIds.add(item["LD ID"]);
        certNums.add(item["Cert Num"]);
        prrNums.add(item["Item ID"]);
      });
      if (allSelected) {
        return { uniqueLDIDs: ["All"], uniqueCertNums: ["All"], uniquePRRIDs: ["All"], uniquePRR1IDs: Array.from(prrNums)};
      }
  
      return { 
        uniqueLDIDs: Array.from(ldIds),
        uniqueCertNums: Array.from(certNums),
        uniquePRRIDs: Array.from(prrNums)
      };
    }, [selectedItems, allSelected]);
  
  const [duration, setDuration] = React.useState("2");          // number
  const [durationUnit, setDurationUnit] = React.useState("weeks"); // "days" | "weeks" | "months"
  const [intervals, setIntervals] = React.useState("4");        // steps
  // Caps requested by spec
  const MAX_DAYS = 90;   // absolute cap in days
  const MAX_STEPS = 10;  // number of price changes

  const unitMax = React.useMemo(() => ({ days: 90, weeks: 12, months: 3 }), []);
  const unitToDays = React.useMemo(() => ({ days: 1, weeks: 7, months: 30 }), []);

  // Clamp duration when unit changes or value out-of-bounds
  React.useEffect(() => {
    const maxForUnit = unitMax[durationUnit] || 90;
    let num = parseInt(duration || "0", 10);
    if (isNaN(num) || num < 1) num = 1;
    if (num > maxForUnit) num = maxForUnit;
    if (String(num) !== String(duration)) setDuration(String(num));
  }, [durationUnit]);

  const exampleStone = selectedItems?.[0] || null;
  
  const getLabels = () => {
    // Mirror BulkRepricing’s label/description behavior, adapted for auto-schedule context
    if (priceType === 0) {
      return {
        label: pricingMethod === "to" ? "Target Price Per Carat ($)" : "Adjust Per Carat ($)",
        placeholder: pricingMethod === "to" ? "4800" : "200",
        help:
          pricingMethod === "to"
            ? "Final per-carat price to reach by the end of the schedule."
            : "Add/subtract this amount per carat from current revised prices (total change over the schedule).",
      };
    }
    if (priceType === 1) {
      return {
        label: pricingMethod === "to" ? "Target Total Price ($)" : "Adjust Total Price ($)",
        placeholder: pricingMethod === "to" ? "5000" : "500",
        help:
          pricingMethod === "to"
            ? "Final total price to reach by the end of the schedule."
            : "Add/subtract this amount from current revised total price (total change over the schedule).",
      };
    }
    return {
      label: pricingMethod === "to" ? "Target RAP Discount (%)" : "Adjust RAP discount (%)",
      placeholder: pricingMethod === "to" ? "-55" : "-1.5",
      help:
        pricingMethod === "to"
          ? "Final RAP% (discount) to reach by the end of the schedule."
          : "Adjust current RAP discount by revised percentage (total change over the schedule).",
    };
  };
  const labels = getLabels();

  // Heuristic probable-rank estimation (preview only)
  const estimateRankForPrice = (stone, price) => {
    const curr = Number(stone["Total Price"]) || 0;
    const delta = (curr - price) / Math.max(1, curr); // positive when lowering
    const marketCount =
      (Array.isArray(stone["Rapnet Price Array"]) && stone["Rapnet Price Array"].length) ||
      Number(stone.count) || 300;
    const currentRank = Number(stone["Rap Rank"]) || Number(stone.rank) || Math.ceil(marketCount / 2);
    const shift = Math.round(delta * marketCount * 0.6);
    const newRank = clamp(currentRank - shift, 1, marketCount);
    return { newRank, deltaRank: newRank - currentRank, marketCount, currentRank };
  };

  // Convert a target to a final TOTAL price for a stone
  const finalTotalForStone = (stone) => {
    if (!stone) return null;
    const cts = Number(stone["Carat"]) || 0;
    const rapList = Number(stone["Rap $/Ct"]) || 0;

    const currTotal = Number(stone["Total Price"]) || 0;
    const currPpc = Number(stone["$/Ct"]) || 0;
    const currRap = Number(stone["Rap %"]) || 0;

    const input = Number(targetValue);
    if (!Number.isFinite(input)) return null;

    // For auto schedules, "to" means final absolute target; "by" means final is current + delta
    if (priceType === 0) {
      // PPC
      if (!cts) return null;
      const finalPpc = pricingMethod === "to" ? input : currPpc + input;
      return finalPpc * cts;
    }
    if (priceType === 1) {
      // Total
      return pricingMethod === "to" ? input : currTotal + input;
    }
    // RAP%
    if (!rapList || !cts) return null;
    const finalRap = pricingMethod === "to" ? input : currRap + input;
    return rapList * (1 + finalRap / 100) * cts;
  };

  // Given a TOTAL price, derive the display metric for the selected priceType
  const metricFromTotal = (stone, total, type) => {
    const cts = Number(stone?.["Carat"]) || 0;
    const rapList = Number(stone?.["Rap $/Ct"]) || 0;
    if (!Number.isFinite(total)) return null;
    if (type === 1) return total; // Total
    if (type === 0) return cts ? total / cts : null; // PPC
    // type === 2 -> RAP%
    if (!cts || !rapList) return null;
    const ppc = total / cts;
    return ((ppc / rapList) - 1) * 100;
  };

   const handleClose = () => {
    setOpen(false);
    // Reset states
    setIsLoading(false);
    setIsProcessing(false);
  };
  // Build schedule (for preview only, based on first stone)
  const calculateSchedule = () => {
    if (!exampleStone) return [];
    const finalTotal = finalTotalForStone(exampleStone);
    const currentTotal = Number(exampleStone["Total Price"]) || Number(exampleStone.currentPrice) || null;

    if (!Number.isFinite(finalTotal) || !Number.isFinite(currentTotal)) return [];

    // Steps clamp
    const requestedSteps = Number.parseInt(intervals || "0", 10);
    let steps = Math.max(1, requestedSteps);
    steps = Math.min(steps, MAX_STEPS);

    // Duration clamp
    const durNum = Math.max(1, Number.parseInt(duration || "0", 10));
    const unitDays = unitToDays[durationUnit] || 1;
    const requestedDays = durNum * unitDays;
    const totalDays = Math.min(requestedDays, MAX_DAYS);

    const clamped = {
      stepsClamped: requestedSteps !== steps,
      daysClamped: requestedDays !== totalDays,
    };

    const changeTotal = finalTotal - currentTotal;
    const changePerInterval = changeTotal / steps;
    const intervalDays = totalDays / steps;

    const out = [];
    for (let i = 1; i <= steps; i++) {
      const price = currentTotal + changePerInterval * i;
      const date = new Date(Date.now() + intervalDays * i * 24 * 60 * 60 * 1000);
      const { newRank, deltaRank, currentRank } = estimateRankForPrice(exampleStone, price);
      out.push({
        step: i,
        price: Math.round(price),
        change: Math.round(changePerInterval),
        date,
        rank: { current: currentRank, next: newRank, delta: deltaRank },
      });
    }

    out._clamped = clamped;
    out._meta = { steps, totalDays };
    return out;
  };

  const schedule = React.useMemo(calculateSchedule, [exampleStone, targetValue, priceType, duration, durationUnit, intervals, pricingMethod]);

  const openDialog = () => setOpen(true);
  const closeDialog = () => setOpen(false);

  const stonesCount = selectedItems.length;


   const handleBulkReprice = async () => {
      try {
        const buttonClickedEvent = {
          event_type: `v4_autorepricin`,
        };
        amplitude.track(buttonClickedEvent);
      } catch {}

      setIsLoading(true);
      handleBulkClose()
      const url = `${process.env.REACT_APP_API_URL}/pcp_bulk_auto_repricing`;
      let data;
      
      const maxUnits = unitMax[durationUnit] || 90;
      const unitDays = unitToDays[durationUnit] || 1;
      const cappedUnits = clamp(Number(duration), 1, maxUnits);
      const durationDays = clamp(cappedUnits * unitDays, 1, MAX_DAYS);

      if (allSelected) {
        data = {
          item_ids: id == "item-details" ? uniquePRR1IDs : [],
          select_all: true,
          is_total_price: priceType == 1 ? true : false,
          is_price_per_carat: priceType == 0 ? true : false,
          is_rap_discount: priceType == 2 ? true : false,
          // Include filters so backend applies full criteria
          item_filter: newFilters.item_filter,
          market_filters: newFilters.market_filters,
          custom_data_filters: newFilters.custom_data_filters,
          ml_filters: newFilters.ml_filters,
          cert_stock_number: searchParam,
          target_value: targetValue,
          reprice_type: pricingMethod,
          duration_in_days: durationDays,
          interval_steps: clamp(Number(intervals), 1, MAX_STEPS),
        };
      } else {
        data = {
          item_ids: id == "item-details" ? uniquePRRIDs : uniqueLDIDs,
          select_all: false,
          is_total_price: priceType == 1 ? true : false,
          is_price_per_carat: priceType == 0 ? true : false,
          is_rap_discount: priceType == 2 ? true : false,
          // Include filters so backend applies full criteria
          item_filter: newFilters.item_filter,
          market_filters: newFilters.market_filters,
          custom_data_filters: newFilters.custom_data_filters,
          ml_filters: newFilters.ml_filters,
          cert_stock_number: searchParam,
          target_value: targetValue,
          reprice_type: pricingMethod,
          duration_in_days: durationDays,
          interval_steps: clamp(Number(intervals), 1, MAX_STEPS),
        };
      }
  
      if (activeTab === 3) {
        data.state = "repriced";
      }
  
      if (activeTab === 1 || activeTab === 2) {
        data.is_filter_locked = activeTab === 1 ? false : true;
      }
  
      let token = Cookies.get("access_token");
  
      try {
        const response = await axios.post(url, data, {
          headers: {
            Authorization: `Bearer ${token}`,
            'ngrok-skip-browser-warning': 'true',
          },
        });
  
        setIsLoading(false);
  
        if (response.data.is_working_in_background) {
          // Background job started — trigger global progress bar
          const reqChannelId = response.data.request_channel_id;
          // For item-details, always pass actual LD IDs for invalidation (even when select-all)
          const invalidateLDs = (id === "item-details")
            ? Array.from(new Set((selectedItems || []).map(it => it["PRR ID"])) ).filter(Boolean)
            : uniqueLDIDs;
          dispatch(startProgress({
            channelId: reqChannelId,
            message: response.data.message,
            invalidateLDIDs: invalidateLDs,
            invalidatePRRIDs: uniquePRRIDs,
            selectedAll: allSelected,
            routeId: id,
            successMessage: 'Auto repricing completed successfully!'
          }));
          setIsProcessing(true);
          setOpen(false);
          dispatch(setToast(true));
          dispatch(setAlertText(response.data.message));
          dispatch(setSeverity('info'));
        } else {
          // Immediate response (small dataset) — refresh data now.
          if (Array.isArray(filters) && filters.length > 0 && accountId) {
            try {
              const args = {
                id: accountId,
                from: page ? pageSize * page : 0,
                size: pageSize,
                asc_desc: sorting?.[0]?.asc_desc ?? 'asc',
                sort_by: sorting?.[0]?.sort_by ?? 'sell_offer_cents',
                search: searchParam,
                sort: sorting,
                state: activeTab === 3 ? 'repriced' : '',
                is_filter_locked: activeTab === 1 ? false : activeTab === 2 ? true : '',
                isSearched: false,
              };
              const body = convertToJsonObject(filters, customFiltersRaw);
              const data = await refetchFiltered({ args, body }).unwrap();
              dispatch({ type: 'pricing/setUserTable', payload: data?.data?.map((it) => mapping(it)) || [] });
              dispatch({ type: 'pricing/setPageCount', payload: data?.total_matching_count || 0 });
            } catch (e) {
              dispatch(apiSlice.util.invalidateTags(["priceTable"]));
            }
          } else {
            dispatch(apiSlice.util.invalidateTags(["priceTable"]));
          }
          if (selectedItems[0]?.itemIDs !== "All") {
            if(id == "item-details"){
                uniquePRRIDs.forEach(id => {
                dispatch(apiSlice.util.invalidateTags([`Rapnet matching ${id}`]));
              });
            }
            uniqueLDIDs.forEach(id => {
              dispatch(apiSlice.util.invalidateTags([`Rapnet matching ${id}`]));
            });
          }
          dispatch(setToast(true));
          dispatch(setAlertText(response.data.message));
          dispatch(setSeverity("success"));
          setIsProcessing(false);
          handleClose();
        }
      } catch (error) {
        setIsLoading(false);
        setIsProcessing(false);
        dispatch(setToast(true));
        dispatch(setAlertText(error.response?.data?.message || "An error occurred"));
        dispatch(setSeverity("error"));
      }
    }

  return (
    <div className="w-max flex justify-center">
    {/* Progress overlay is global via <GlobalProgressBar /> */}
      <div
        className={cn(
          "self-end  flex gap-[0.200rem] items-center justify-center h-8  text-[#1E90FF] font-medium text-[0.75rem] w-max",
          stonesCount > 0 ? "bg-transparent text-[#1E90FF]" : "bg-transparent opacity-40 border-opacity-40 cursor-not-allowed text-[#1E90FF]"
        )}
        onClick={openDialog}
        disabled={stonesCount === 0}
        type="button"
      >
        <Clock className="h-4 w-4" />
       { id== "User Diamond" ? "Auto Price" :"Bulk Auto Price"} 
      </div>

      <Dialog
        open={open}
        onClose={closeDialog}
        PaperProps={{
          className: "p-2",
          sx: {
            width: '780px',
            maxWidth: '780px',
            minWidth: '780px',
            maxHeight: '80vh',
            display:'flex',
            flexDirection:'column',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            mb: 2, borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingDown className="h-5 w-5" /> Bulk Auto Pricing
          </Typography>
          <Chip label={`${allSelected? 'All' : stonesCount} selected`} variant="outlined" />
        </DialogTitle>

        <DialogContent sx={{ overflow: 'auto', p: 3, scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }}>
         
          <Grid container spacing={4}>
            {/* Left: Configuration */}
            <Grid item xs={12} lg={7}>
              <Box
                sx={{
                  display: 'flex', flexDirection: 'column', gap: 3,
                  width: '100%', minWidth: "273px"
                }}
              >
                <Box>
                  <p className='mb-2 text-sm font-semibold text-gray-700'>
                    Price Type
                  </p>
                  <PriceTypeTabs value={priceType} onChange={(e, v) => setPriceType(v)} />
                </Box>

                <div  className='flex flex-col w-full'>
                <p className='mb-3 text-sm font-semibold text-gray-700'>
                  Pricing Method
                </p>
               <div className="flex flex-row items-center gap-4 gap">
                    <label className="flex text-gray-700 items-center gap-2 text-[0.8rem] font-medium cursor-pointer">
                      <input
                        type="radio"
                        name="pricingMethod"
                        value="to"
                        checked={pricingMethod === "to"}
                        onChange={(e) => setPricingMethod(e.target.value)}
                        className="w-[0.85rem] h-[0.85rem] accent-blue-600"
                      />
                      Set price to
                    </label>

                    <label className="flex text-gray-700 items-center gap-2 text-[0.8rem] font-medium cursor-pointer">
                      <input
                        type="radio"
                        name="pricingMethod"
                        value="by"
                        checked={pricingMethod === "by"}
                        onChange={(e) => setPricingMethod(e.target.value)}
                        className="w-[0.85rem] h-[0.85rem] accent-blue-600"
                      />
                      Adjust price by
                    </label>
                  </div>
                </div>

                {/* TARGET VALUE */}
                <div className="space-y-4 mb-2">
                  <div className="h-[74px]">
                    <label htmlFor="targetValue" className="block text-xs font-medium text-gray-700">
                      {labels.label}
                    </label>
                    <input
                      id="targetValue"
                      type="number"
                      placeholder={labels.placeholder}
                      value={targetValue}
                      onChange={(e) => setTargetValue(e.target.value)}
                      className="mt-1 block w-full h-8 rounded-md border border-gray-300 px-3 py-2 text-xs
                                focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {/* helper text (min-height ~40px + slight left padding like your sx) */}
                    <p className="mt-1 text-[0.65rem] text-gray-500 min-h-10 pl-1">
                      {labels.help}
                    </p>
                  </div>

                  {/* DURATION + UNIT */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label htmlFor="duration" className="block text-xs font-medium text-gray-700">
                        Duration
                      </label>
                      <input
                        id="duration"
                        type="number"
                        min={1}
                        max={unitMax[durationUnit]}
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        className="mt-1 block w-full h-9 rounded-md border border-gray-300 px-3 py-2 text-xs
                                  focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="durationUnit" className="block text-xs font-medium text-gray-700">
                        Unit
                      </label>
                      <select
                        id="durationUnit"
                        value={durationUnit}
                        onChange={(e) => setDurationUnit(e.target.value)}
                        className="mt-1 block w-full h-9 rounded-md border border-gray-300 px-3 py-2 text-xs bg-white
                                  focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="days">Days</option>
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                      </select>
                    </div>
                  </div>

                  {/* NUMBER OF PRICE CHANGES */}
                  <div>
                    <label htmlFor="intervals" className="block text-xs font-medium text-gray-700">
                      Number of Price Changes
                    </label>
                    <input
                      id="intervals"
                      type="number"
                      min={1}
                      max={MAX_STEPS}
                      value={intervals}
                      onChange={(e) => setIntervals(e.target.value)}
                      className="mt-1 block w-full h-9 rounded-md border border-gray-300 px-3 py-2 text-xs
                                focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-[0.65rem] text-gray-500 min-h-10 pl-1">
                      System will spread adjustments evenly across steps.
                    </p>

                    {/* clamp warnings (same logic as your IIFE) */}
                    {(() => {
                      const steps     = parseInt(intervals || '0', 10);
                      const durNum    = parseInt(duration || '0', 10);
                      const unitDays  = (unitToDays && unitToDays[durationUnit]) || 1;
                      const totalDays = (isNaN(durNum) ? 0 : durNum) * unitDays;

                      const stepsClamped = steps > MAX_STEPS;
                      const daysClamped  = totalDays > MAX_DAYS;

                      if (!stepsClamped && !daysClamped) return null;

                      return (
                        <p className="mt-1 text-xs text-amber-700">
                          {daysClamped ? `Duration capped to ${MAX_DAYS} days (e.g., 12 weeks or 3 months). ` : ''}
                          {stepsClamped ? `Number of price changes capped to ${MAX_STEPS}.` : ''}
                        </p>
                      );
                    })()}
                  </div>
                </div>

              </Box>
            </Grid>

            {/* Right: Preview */}
            <Grid item xs={12} lg={5}>
              <div className='flex flex-col gap-2 max-h-[390px]'>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <p className='text-sm font-semibold text-gray-700'>
                    Preview
                  </p>
                  {exampleStone ? (
                    <p className='text-xs text-gray-600 font-medium '>{`Example: ${exampleStone["Stock No"] || exampleStone["LD ID"] || exampleStone.id || "Stone"} • ${exampleStone["Carat"] || exampleStone.carat || "—"}ct`}</p>
                     
                  ) : null}
                </Box>

                {!exampleStone ? (
                  <p className='text-[0.69rem] text-gray-600'>
                    Select stones and configure to see the auto-pricing schedule and probable rank changes.
                  </p>
                ) : (
                  targetValue == "" ?
                   <p className='text-[0.69rem] text-gray-600'>
                    Add input value to see the auto-pricing steps & schedules and probable rank changes.
                  </p>
                   :
                  <>
                    <Box sx={{ maxHeight: 560, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5, msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 1, height: 0 } }}>
                      {schedule.map((item, idx) => {
                        const currentTotal = Number(exampleStone["Total Price"]) || Number(exampleStone.currentPrice) || null;
                        const prevTotal = idx === 0 ? currentTotal : schedule[idx - 1].price;
                        const displayVal = metricFromTotal(exampleStone, item.price, priceType);
                        const prevDisplay = metricFromTotal(exampleStone, prevTotal, priceType);
                        const displayDelta = (Number.isFinite(displayVal) && Number.isFinite(prevDisplay)) ? (displayVal - prevDisplay) : null;

                        const valueLabel = `New ${labelFor(priceType)}`;

                        const renderValue = () => {
                          if (!Number.isFinite(displayVal)) return '—';
                          if (priceType === 2) return `${displayVal.toFixed(2)}%`;
                          if (priceType === 0) return fmtMoney(displayVal, { decimals: 2 });
                          return fmtMoney(displayVal);
                        };

                        const withSignMoney = (value, opts) => {
                          const sign = value > 0 ? '+' : value < 0 ? '-' : '';
                          return `${sign}${fmtMoney(Math.abs(value), opts)}`; // sign comes before $
                        };

                        const renderDelta = () => {
                          if (!Number.isFinite(displayDelta)) return 'Change: —';

                          if (priceType === 2) {
                            const sign = displayDelta > 0 ? '+' : displayDelta < 0 ? '-' : '';
                            return `Change: ${sign}${Math.abs(displayDelta).toFixed(2)}%`;
                          }

                          if (priceType === 0) {
                            return `Change: ${withSignMoney(displayDelta, { decimals: 2 })}`;
                          }

                          return `Change: ${withSignMoney(displayDelta)}`;
                        };

                        return (
                        <Box key={item.step} sx={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <div label={`Step ${item.step}`} className=' text-[0.65rem] border-[1px] text-blue-600 border-solid border-blue-500 px-2 rounded-lg' >{`Step ${item.step}`}</div>
                            <p className=' text-[0.69rem] text-gray-600 font-semibold'>
                              {item.date.toLocaleDateString()} • {item.date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </p>
                          </Box>

                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <p className='text-[0.69rem] text-gray-600'>{valueLabel}</p>
                              <h6 className='text-[0.79rem] flex items-center justify-start font-semibold '>{renderValue()}</h6>
                              <p className='mt-1 text-[0.69rem]' variant="caption" color="text.secondary">{renderDelta()}</p>
                            </Grid>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <p className='text-[0.69rem] text-gray-600'>Probable Rank</p>
                              <h6 className='text-[0.79rem] flex items-center justify-end font-semibold '>
                                #{item.rank.current} → #{item.rank.next}{" "}
                                
                              </h6>
                              <p className='text-[0.69rem] mt-1' >
                                 Rank Change: <span className={item.rank.delta < 0 ? "text-green-600" : "text-red-600"}>({item.rank.delta > 0 ? "+" : ""}{item.rank.delta})</span>
                              </p>
                              {/* <p className='text-xs  '>market size & price delta</p> */}
                            </Grid>
                          </Grid>
                        </Box>
                        );
                      })}
                    </Box>

                    {(schedule?._clamped?.stepsClamped || schedule?._clamped?.daysClamped) && (
                      <Typography variant="caption" sx={{ color: '#9A6E00' }}>
                        Note: schedule capped to {schedule?._meta?.steps ?? ""} steps over {schedule?._meta?.totalDays ?? ""} days.
                      </Typography>
                    )}
                  </>
                )}
              </div>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
            <button
                className={cn(
                    "rounded-md flex gap-[0.200rem] items-center justify-center px-3 h-8 border-solid border-[1px] border-[#1E90FF] text-[#1E90FF] font-medium text-[0.75rem] w-max bg-transparent",
                    isProcessing && "opacity-50 cursor-not-allowed"
                )}
                onClick={closeDialog}
                disabled={isLoading || isProcessing}
                >
                Cancel
            </button>
            <button
                className={cn(
                    "rounded-md flex gap-[0.200rem] items-center justify-center px-4 hover:bg-blue-300 h-8 border-none font-medium text-[0.75rem] w-max",
                    (isLoading || isProcessing || targetValue === "" || isNaN(targetValue))
                    ? "bg-blue-300 cursor-not-allowed"
                    : "bg-[#A4D9FF] hover:bg-blue-300"
                )}
                onClick={() => setConfirmOpen(true)} 
                type="submit" 
                disabled={isLoading || isProcessing || targetValue === "" || isNaN(targetValue)}
                >
                {isLoading ? (
                    <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Loading...
                    </>
                ) : isProcessing ? (
                    <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Processing...
                    </>
                ) : (
                    "Schedule Auto Pricing"
                )}
            </button>    
          {/* <MUIButton
            onClick={closeDialog}
            size="small"
            disabled={stonesCount === 0 || (schedule?.length ?? 0) === 0}
            sx={{ background: '#A4D9FF', '&:hover': { background: '#90cdf9' } }}
          >
            <BadgePercent className="h-4 w-4" style={{ marginRight: 6 }} />
            Schedule Auto Pricing
          </MUIButton> */}
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmOpen}
        onClose={() => { if (!isLoading && !isProcessing) setConfirmOpen(false); }}
        PaperProps={{ sx: { maxWidth: 480, width: 400 } }}
      >
        <div className="flex items-center justify-between px-6 pt-4 pb-2 border border-b border-gray-200 ">
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            Confirm Bulk Auto Pricing
          </h2>
          <div
            onClick={handleClose}
            disabled={isLoading || isProcessing}
            className=" transition-colors"
          >
            <X size={20} />
          </div>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="border border-amber-700 bg-amber-50  rounded-lg p-3 flex items-start justify-between gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-600  flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900 ">
              This action cannot be undone. Prices will be updated on schedule immediately.
            </p>
          </div>

          <div className="space-y-3">
            <p className="text-xs text-slate-500 ">
              You are about to schedule bulk auto pricing for:
            </p>

            <div className="bg-slate-50  rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between ">
                <span className="text-sm font-medium ">
                  Stones affected
                </span>
                <p className="text-sm font-medium text-slate-900 ">
                  {selectedItems.length > 0 ? (allSelected ? 'all selected' : (id==="item-details" ? `${uniquePRRIDs?.length || 0}` : `${uniqueLDIDs?.length || 0}`)) : 0} stones
                </p>
              </div>
              <hr className="border-t border-gray-600 " />

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs  ">
                    Pricing type
                  </span>
                  <span className="text-xs  ">
                    {priceType === 0 ? 'Price per Carat' : priceType === 1 ? 'Total Price' : 'Rap %'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs  ">
                    Method
                  </span>
                  <span className="text-xs  ">
                    {pricingMethod === 'to' ? 'Set to' : 'Adjust by'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs ">
                    Value
                  </span>
                  <span className="text-xs font-semibold">
                    {(priceType == 0 || priceType == 1) ? `${Number(targetValue) >= 0 ? '+' : '-'}$${Math.abs(Number(targetValue) || 0)}` : String(targetValue)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs ">
                    Duration
                  </span>
                  <span className="text-xs ">
                    {duration} {durationUnit}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs ">
                    Price changes
                  </span>
                  <span className="text-xs ">
                    {intervals}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border border-t border-slate-200">
          <div
            onClick={() => setConfirmOpen(false)}
            disabled={isLoading || isProcessing}
            className='bg-transparent  rounded-md text-[0.8rem] px-3 py-[0.18rem] font-medium text-black border-solid border-[1.25px] cursor-pointer'
          >
            Cancel
          </div>
          <div
            onClick={() => {handleBulkClose(); setConfirmOpen(false); handleBulkReprice(); }}
            disabled={isLoading || isProcessing || targetValue === "" || isNaN(targetValue)}
            className='bg-blue-500 rounded-md text-[0.8rem] px-3 py-1 font-medium text-white cursor-pointer'
          >
            Confirm & Apply
          </div>
        </div>
      </Dialog>
    </div>
  );
}
